<template>
  <div v-if="isTypeDispatcher">
    <re-select
      ref="valuerSelect"
      :model-value="props.row.valuer?.username ?? 'n/a'"
      :disable="disableSelect"
      :options="[
        { label: $t('monolith.dashboard.unassigned'), value: 'n/a' },
        ...valuers.data?.map((val) => ({ value: val.username, label: val.email })),
      ]"
      :bg-color="valuerSelectColor ? `${valuerSelectColor}-light` : undefined"
      :loading="valuers.isBusy || request.isBusy"
      v-bind="$attrs"
      size="md"
      class="q-pb-none"
      @update:model-value="handleSelectValuer"
    />
  </div>
  <q-badge
    v-else-if="props.row.valuer?.username && !isMobile"
    :color="valuerSelectColor"
  >
    {{ props.row.valuer?.username }}
  </q-badge>
  <div
    v-if="isMobile"
    class="row justify-between"
  >
    <div v-if="props.row.valuer?.username">
      <re-icon
        name="mdi-account"
        class="q-mr-xs"
        color="grey"
      />
      <span>{{ props.row.valuer?.username }}</span>
    </div>
    <div v-if="props.row.latest_appointment">
      <re-icon
        name="mdi-calendar-account"
        class="q-mr-xs"
        color="grey"
      />
      <span>
        {{ $format.appointment(props.row.latest_appointment) }}
      </span>
    </div>
  </div>

  <re-dialog
    v-if="isTypeDispatcher"
    v-model="newValuerConfirm"
    has-cancel
    @confirm="updateValuer(newValuerCandidate)"
    @cancel="handleCancelNewValuer"
  >
    {{ $t('monolith.dashboard.valuer_change_modal.content') }}
  </re-dialog>
</template>
<script setup>
import { useQueryClient } from '@tanstack/vue-query'
import { useQuasar } from 'quasar'
import { computed, ref } from 'vue'

import { useCurUser } from '@/composables/useCurUser'
import { useRequest } from '@/composables/useRequest'
import useUserList from '@/composables/useUserList'
import { useGlobals } from '@/main.js'
import enums from '@/plugins/enums'

// don't automatically insert attrs in root div:
defineOptions({ inheritAttrs: false })

const props = defineProps({
  row: {
    type: Object,
    required: true,
  },
})

const disableSelect = computed(() => {
  // In the white label case, when upgrading from DVM to OVM, the valuer will be erased. The white label user still needs to assign the valuation to themself
  if (props.row.is_ovm_whitelabel) return false

  return (
    !valuers.data ||
    request.isBusy ||
    props.row.valuation_type == 'ovm' ||
    props.row.payment_summary?.is_payment_completed === false
  )
})

const queryClient = useQueryClient()
const { hasRole } = useCurUser()
const { $format } = useGlobals()
const q = useQuasar()
const request = useRequest({
  requestRefInit: props.row.valuation_request_ref,
  startDisabled: true,
})
const valuers = useUserList({ roles: 'valuer', modules: props.row.valuation_type })
const newValuerConfirm = ref(null)
const valuerSelect = ref(null)

const isMobile = computed(() => q.screen.lt.md)
const isTypeDispatcher = computed(
  // TODO: should we also support onsite expert update from the Dashboard?
  () =>
    hasRole('dispatcher', props.row.valuation_type) &&
    (props.row.valuation_type === 'dvm' || props.row.is_ovm_whitelabel)
)

const newValuerCandidate = ref(null)

const handleSelectValuer = (newValuer) => {
  if (!props.row.valuer?.username) return updateValuer(newValuer)
  // if a valuer is already assigned, show confirmation dialog
  newValuerConfirm.value = true
  newValuerCandidate.value = newValuer
}

const handleCancelNewValuer = () => {
  valuerSelect.value?.cancel?.()
  newValuerConfirm.value = null
  newValuerCandidate.value = null
}

const updateValuer = (newValuer) => {
  const isRequestAssigned = !!props.row.valuer?.username
  if (newValuer === 'n/a') request.updateValuer({ release: true })
  else if (isRequestAssigned) request.updateValuer({ username: newValuer, steal: true })
  else request.updateValuer({ username: newValuer, steal: false })
  // Invalidate workload query to refresh counts
  queryClient.invalidateQueries({
    queryKey: ['workload'],
  })
}

// TODO: make q-select display colour even when not selected
const valuerSelectColor = computed(() => {
  if (!hasRole('valuer') || props.row.status !== 'submitted') return undefined
  if (!props.row.valuer) return enums.feedbackType.POSITIVE
  if (props.row.valuer?.is_self) return enums.feedbackType.PRIMARY
  return enums.feedbackType.NEGATIVE
})
</script>
